import type { CommandEnvelope } from '../engine/simulation/commands';
import type { GameConfig, SaveData } from '../engine/simulation/simulation';

/** Network protocol version; peers with different versions refuse to play together. */
export const PROTOCOL_VERSION = 2;

export interface LobbySlot {
  peerId: string | null; // null for AI
  name: string;
  civ: string;
  team: number;
  /** Player colour (one of PLAYER_COLORS); unique within the lobby. */
  color: number;
  ready: boolean;
  ai: { difficulty: 'beginner' | 'standard' | 'hard' | 'expert'; personality: 'balanced' | 'aggressive' | 'defensive' | 'economic' | 'technological' | 'expansionist' } | null;
}

export interface LobbySettings {
  mapType: GameConfig['mapType'];
  mapSize: GameConfig['mapSize'];
  seed: number;
  startingAge: number;
  /** Game clock speed (see GameConfig.gameSpeed). */
  gameSpeed?: number;
  startingResources: GameConfig['startingResources'];
  victory: GameConfig['victory']['conditions'];
  maxPlayers: number;
}

export interface LobbyState {
  hostId: string;
  slots: LobbySlot[];
  settings: LobbySettings;
}

export type NetMessage =
  | { type: 'hello'; from: string; name: string; protocol: number; simVersion: number }
  | { type: 'lobby'; from: string; state: LobbyState }
  | { type: 'slot'; from: string; civ: string; team: number; ready: boolean; name: string; color: number }
  | { type: 'start'; from: string; config: GameConfig; players: (string | null)[]; inputDelay: number }
  | { type: 'batch'; from: string; player: number; tick: number; cmds: CommandEnvelope[] }
  | { type: 'hash'; from: string; player: number; tick: number; hash: number }
  | { type: 'peerLost'; from: string; lost: number; last: number; batches: { tick: number; cmds: CommandEnvelope[] }[] }
  | { type: 'resyncRequest'; from: string; tick: number }
  | { type: 'resync'; from: string; tick: number; save: SaveData }
  | { type: 'chat'; from: string; text: string; player: number; allies: boolean; x?: number; z?: number }
  /** fps and tick: the sender's frame rate and game tick, for the per-player connection panel. */
  | { type: 'ping'; from: string; t: number; fps?: number; tick?: number }
  | { type: 'pong'; from: string; t: number }
  | { type: 'leave'; from: string }
  // Reconnection (see LockstepTransport): a connection to `player` was lost or came back; the
  // deciding player chose to keep waiting another cycle, or to carry on without the lost players.
  | { type: 'connLost'; from: string; player: number }
  | { type: 'connBack'; from: string; player: number }
  | { type: 'keepWaiting'; from: string }
  | { type: 'dropDecision'; from: string; lost: number[] };

/** Defensive structural validation for anything arriving off the wire. */
export function isNetMessage(m: unknown): m is NetMessage {
  if (!m || typeof m !== 'object') return false;
  const o = m as Record<string, unknown>;
  if (typeof o.type !== 'string' || typeof o.from !== 'string' || o.from.length > 64) return false;
  switch (o.type) {
    case 'batch':
      return Number.isInteger(o.player) && Number.isInteger(o.tick) && Array.isArray(o.cmds) && (o.cmds as unknown[]).length <= 64 &&
        (o.cmds as unknown[]).every((c) => {
          const e = c as Record<string, unknown>;
          return e && Number.isInteger(e.tick) && Number.isInteger(e.player) && Number.isInteger(e.seq) && typeof e.cmd === 'object' && e.cmd !== null;
        });
    case 'hash':
      return Number.isInteger(o.player) && Number.isInteger(o.tick) && typeof o.hash === 'number';
    case 'chat':
      return typeof o.text === 'string' && o.text.length <= 500;
    case 'connLost':
    case 'connBack':
      return Number.isInteger(o.player);
    case 'dropDecision':
      return Array.isArray(o.lost) && (o.lost as unknown[]).length <= 8 && (o.lost as unknown[]).every((p) => Number.isInteger(p));
    default:
      return true;
  }
}
